/**
 * ContentBlockWidget Component
 *
 * Renders rich text content with TrixTextEditor for inline editing.
 * Uses the same TrixTextEditor as the existing UI-Model for consistency.
 */

import React, { useState } from 'react';
import { ContentBlockWidgetProps } from './types';
import { CONTENT_BLOCK_DEFAULT_CONFIG } from '../../config/widgetDefaults';
import TrixTextEditor from 'Pages/GMS/ISR/Components/TrixTextEditor';
import { useUIModelApi } from '../../contexts/UIModelApiContext';
import AppSettings from 'Settings/AppSettings';

export const ContentBlockWidget = ({
  widget,
  isSelected,
  onSelect,
  onContentChange,
}: ContentBlockWidgetProps) => {
  // Get configureId for image upload URL
  const { config: apiConfig } = useUIModelApi();
  const configureId = apiConfig?.configureId || '';

  // Get content from widget root (preferred) with fallback to configuration for backward compatibility
  const widgetConfig = (widget.configuration || {}) as Partial<typeof CONTENT_BLOCK_DEFAULT_CONFIG>;
  const initialContent = widget.content || (widget.configuration?.content as string) || widgetConfig.htmlContent || '';

  // Local state for unsaved changes
  const [localContent, setLocalContent] = useState(initialContent);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Merge with defaults
  const mergedConfig = {
    ...CONTENT_BLOCK_DEFAULT_CONFIG,
    ...widgetConfig,
  };

  const {
    showBorder,
    backgroundColor,
    padding,
  } = mergedConfig;

  // Build container styles
  const containerStyle: React.CSSProperties = {
    backgroundColor: backgroundColor === 'transparent' ? undefined : backgroundColor,
    padding: padding,
  };

  // Handle content change from TrixEditor
  const handleContentChange = (newContent: string) => {
    setLocalContent(newContent);
    setHasUnsavedChanges(true);
  };

  // Handle Done button click - save changes
  const handleDoneClick = () => {
    if (onContentChange && hasUnsavedChanges) {
      onContentChange(localContent);
      setHasUnsavedChanges(false);
    }
  };

  // Build attachment URL for image uploads
  const attachmentUrl = `${AppSettings.GMS_BACKEND_API_URL}isr/models/${configureId}/image/upload`;

  return (
    <div role="button" data-testid="widgets-contentblockwidget-div-1"
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className={`tw-w-full tw-cursor-pointer tw-transition-all tw-rounded ${
        showBorder ? 'tw-border tw-border-edge' : ''
      } ${isSelected ? 'tw-ring-2 tw-ring-accent tw-ring-offset-2' : ''}`}
      style={containerStyle}
    >
      <div className="tw-text-sm tw-text-ink-secondary tw-bg-surface-sunken tw-rounded-md tw-p-4">
        <TrixTextEditor
          id={`content-block-editor-${widget.id}`}
          updateInfo={handleContentChange}
          value={localContent}
          selectedItemId={widget.id}
          attachmentUrl={attachmentUrl}
        />
        {hasUnsavedChanges && (
          <div className="tw-mt-3 tw-flex tw-justify-end">
            <button data-testid="widgets-contentblockwidget-button-1"
              onClick={(e) => {
                e.stopPropagation();
                handleDoneClick();
              }}
              className="tw-px-4 tw-py-1.5 tw-bg-accent-bold tw-text-ink-inverse tw-text-sm tw-font-medium tw-rounded hover:tw-bg-accent-bold-hover tw-transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentBlockWidget;
